/**
 * Mix a hex (or rgb/rgba) color toward white.
 * @param color CSS color string
 * @param amount 0–1 fraction toward white (e.g. 0.35 = 35% lighter)
 */
export function lightenColor(color: string, amount = 0.35): string {
  if (!color || typeof color !== 'string') {
    return '';
  }

  const trimmed = color.trim();
  const rgb = parseToRgb(trimmed);
  if (!rgb) {
    return trimmed;
  }

  const mix = Math.min(Math.max(amount, 0), 1);
  const r = Math.round(rgb.r + (255 - rgb.r) * mix);
  const g = Math.round(rgb.g + (255 - rgb.g) * mix);
  const b = Math.round(rgb.b + (255 - rgb.b) * mix);

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * The text color to use on a button: the configured one, unless it is too close
 * to the background to see (contrast below 1.5:1), then black or white.
 * The bar is low on purpose: brand pairs like white on teal (~2.6:1) stay as set.
 * Company admins can set the font and background to the same color.
 */
export function readableTextColor(text: string, background: string): string {
  const fg = text ? parseToRgb(text.trim()) : null;
  const bg = background ? parseToRgb(background.trim()) : null;
  if (!fg || !bg) {
    return text;
  }
  const bgLum = relativeLuminance(bg);
  if (contrastRatio(relativeLuminance(fg), bgLum) >= 1.5) {
    return text;
  }
  return contrastRatio(0, bgLum) >= contrastRatio(1, bgLum) ? '#000000' : '#ffffff';
}

function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

function parseToRgb(color: string): { r: number; g: number; b: number } | null {
  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex.split('').map((c) => c + c).join('');
    }
    if (hex.length !== 6 || /[^0-9a-fA-F]/.test(hex)) {
      return null;
    }
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  const rgbMatch = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (rgbMatch) {
    return {
      r: clampByte(+rgbMatch[1]),
      g: clampByte(+rgbMatch[2]),
      b: clampByte(+rgbMatch[3]),
    };
  }

  return null;
}

function toHex(value: number): string {
  return clampByte(value).toString(16).padStart(2, '0');
}

function clampByte(value: number): number {
  return Math.min(255, Math.max(0, value | 0));
}
